"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useState } from "react";
import { TabToggle } from "@/components/common/TabToggle";
import type { ApiWave } from "@/generated/models/ApiWave";
import { MyStreamWaveTab } from "@/types/waves.types";
import { useContentTab, WaveVotingState } from "../ContentTabContext";
import { useWave } from "@/hooks/useWave";
import { useWaveTimers } from "@/hooks/useWaveTimers";
import { ApiWaveType } from "@/generated/models/ApiWaveType";
import { useDecisionPoints } from "@/hooks/waves/useDecisionPoints";
import { Time } from "@/helpers/time";
interface MyStreamWaveDesktopTabsProps {
  readonly activeTab: MyStreamWaveTab;
  readonly wave: ApiWave;
  readonly setActiveTab: (tab: MyStreamWaveTab) => void;
}
interface TabOption {
  key: MyStreamWaveTab;
  label: string;
  panelId: string;
}
const getContentTabPanelId = (tab: MyStreamWaveTab): string => `my-stream-wave-tabpanel-${tab.toLowerCase()}`;
const AUTO_EXPAND_LIMIT = 5;
const MyStreamWaveDesktopTabs: React.FC<MyStreamWaveDesktopTabsProps> = t0 => {
  const $ = _c(52);
  const {
    activeTab,
    wave,
    setActiveTab
  } = t0;
  const {
    availableTabs,
    updateAvailableTabs,
    setActiveContentTab
  } = useContentTab();
  const {
    isChatWave,
    isMemesWave,
    pauses: t1
  } = useWave(wave);
  const {
    filterDecisionsDuringPauses
  } = t1;
  const {
    voting: t2,
    decisions: t3
  } = useWaveTimers(wave);
  const {
    isUpcoming,
    isCompleted,
    isInProgress
  } = t2;
  const {
    firstDecisionDone
  } = t3;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      initialPastWindow: 3,
      initialFutureWindow: 10
    };
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  const {
    allDecisions,
    hasMoreFuture,
    loadMoreFuture
  } = useDecisionPoints(wave, t4);
  let t5;
  if ($[1] !== allDecisions || $[2] !== filterDecisionsDuringPauses) {
    const decisionsAsApiFormat = allDecisions.map(_temp);
    const filtered = filterDecisionsDuringPauses(decisionsAsApiFormat);
    t5 = allDecisions.filter(decision_0 => filtered.some(f => f.decision_time === decision_0.timestamp));
    $[1] = allDecisions;
    $[2] = filterDecisionsDuringPauses;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const filteredDecisions = t5;
  let t6;
  if ($[4] !== filteredDecisions) {
    t6 = filteredDecisions.find(_temp2)?.timestamp ?? null;
    $[4] = filteredDecisions;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const nextDecisionTime = t6;
  const [autoExpandFutureAttempts, setAutoExpandFutureAttempts] = useState(0);
  let t7;
  let t8;
  if ($[6] !== autoExpandFutureAttempts || $[7] !== hasMoreFuture || $[8] !== loadMoreFuture || $[9] !== nextDecisionTime) {
    t7 = () => {
      const hasUpcoming = !!nextDecisionTime;
      if (hasUpcoming) {
        if (autoExpandFutureAttempts !== 0) {
          setAutoExpandFutureAttempts(0);
        }
        return;
      }
      if (!hasMoreFuture) {
        if (autoExpandFutureAttempts !== 0) {
          setAutoExpandFutureAttempts(0);
        }
        return;
      }
      if (autoExpandFutureAttempts >= AUTO_EXPAND_LIMIT) {
        return;
      }
      const timeoutId = globalThis.setTimeout(() => {
        setAutoExpandFutureAttempts(_temp3);
        loadMoreFuture();
      }, 50);
      return () => {
        clearTimeout(timeoutId);
      };
    };
    t8 = [nextDecisionTime, hasMoreFuture, loadMoreFuture, autoExpandFutureAttempts];
    $[6] = autoExpandFutureAttempts;
    $[7] = hasMoreFuture;
    $[8] = loadMoreFuture;
    $[9] = nextDecisionTime;
    $[10] = t7;
    $[11] = t8;
  } else {
    t7 = $[10];
    t8 = $[11];
  }
  useEffect(t7, t8);
  let t9;
  if ($[12] !== firstDecisionDone || $[13] !== isChatWave || $[14] !== isCompleted || $[15] !== isMemesWave || $[16] !== isUpcoming || $[17] !== updateAvailableTabs || $[18] !== wave) {
    t9 = () => {
      const votingState = isUpcoming ? WaveVotingState.NOT_STARTED : isCompleted ? WaveVotingState.ENDED : WaveVotingState.ONGOING;
      updateAvailableTabs(wave ? {
        isMemesWave,
        isChatWave,
        votingState,
        hasFirstDecisionPassed: firstDecisionDone
      } : null);
    };
    $[12] = firstDecisionDone;
    $[13] = isChatWave;
    $[14] = isCompleted;
    $[15] = isMemesWave;
    $[16] = isUpcoming;
    $[17] = updateAvailableTabs;
    $[18] = wave;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== firstDecisionDone || $[21] !== isChatWave || $[22] !== isCompleted || $[23] !== isInProgress || $[24] !== isMemesWave || $[25] !== isUpcoming || $[26] !== updateAvailableTabs || $[27] !== wave) {
    t10 = [wave, isMemesWave, isChatWave, isUpcoming, isCompleted, isInProgress, firstDecisionDone, updateAvailableTabs];
    $[20] = firstDecisionDone;
    $[21] = isChatWave;
    $[22] = isCompleted;
    $[23] = isInProgress;
    $[24] = isMemesWave;
    $[25] = isUpcoming;
    $[26] = updateAvailableTabs;
    $[27] = wave;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  useEffect(t9, t10);
  let t11;
  if ($[29] !== setActiveContentTab || $[30] !== wave?.wave?.type) {
    t11 = () => {
      if (wave?.wave?.type === ApiWaveType.Chat) {
        setActiveContentTab(MyStreamWaveTab.CHAT);
      }
    };
    $[29] = setActiveContentTab;
    $[30] = wave?.wave?.type;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  const t12 = wave?.wave?.type;
  let t13;
  if ($[32] !== setActiveContentTab || $[33] !== t12) {
    t13 = [t12, setActiveContentTab];
    $[32] = setActiveContentTab;
    $[33] = t12;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  useEffect(t11, t13);
  let t14;
  if ($[35] !== availableTabs || $[36] !== isMemesWave) {
    const tabLabels = {
      [MyStreamWaveTab.CHAT]: "Chat",
      [MyStreamWaveTab.LEADERBOARD]: "Leaderboard",
      [MyStreamWaveTab.WINNERS]: "Winners",
      [MyStreamWaveTab.OUTCOME]: "Outcome",
      [MyStreamWaveTab.MY_VOTES]: "My Votes",
      [MyStreamWaveTab.FAQ]: "FAQ"
    };
    let t15;
    if ($[38] !== isMemesWave) {
      t15 = tab => isMemesWave || ![MyStreamWaveTab.MY_VOTES, MyStreamWaveTab.FAQ].includes(tab);
      $[38] = isMemesWave;
      $[39] = t15;
    } else {
      t15 = $[39];
    }
    t14 = availableTabs.filter(t15).map(tab_0 => ({
      key: tab_0,
      label: tabLabels[tab_0],
      panelId: getContentTabPanelId(tab_0)
    }));
    $[35] = availableTabs;
    $[36] = isMemesWave;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  const options = t14;
  let t15;
  let t16;
  if ($[40] !== activeTab || $[41] !== isMemesWave || $[42] !== options || $[43] !== setActiveTab) {
    t15 = () => {
      if (!isMemesWave && [MyStreamWaveTab.MY_VOTES, MyStreamWaveTab.FAQ].includes(activeTab) && options.length > 0) {
        setActiveTab(options[0]?.key);
      }
    };
    t16 = [isMemesWave, activeTab, options, setActiveTab];
    $[40] = activeTab;
    $[41] = isMemesWave;
    $[42] = options;
    $[43] = setActiveTab;
    $[44] = t15;
    $[45] = t16;
  } else {
    t15 = $[44];
    t16 = $[45];
  }
  useEffect(t15, t16);
  if (isChatWave) {
    return null;
  }
  let t17;
  if ($[46] !== setActiveTab) {
    t17 = key => setActiveTab(key as MyStreamWaveTab);
    $[46] = setActiveTab;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  let t18;
  if ($[48] !== activeTab || $[49] !== options || $[50] !== t17) {
    t18 = <div className="tw-@container/tabs tw-px-2 sm:tw-px-4 tw-flex tw-items-start tw-gap-4 tw-justify-between tw-w-full tw-overflow-x-auto tw-scrollbar-thumb-iron-500 tw-scrollbar-track-iron-800 hover:tw-scrollbar-thumb-iron-300 tw-scrollbar-thin"><TabToggle options={options} activeKey={activeTab} onSelect={t17} /></div>;
    $[48] = activeTab;
    $[49] = options;
    $[50] = t17;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  return t18;
};
export default MyStreamWaveDesktopTabs;
function _temp(decision) {
  return {
    decision_time: decision.timestamp
  } as any;
}
function _temp2(decision_1) {
  return decision_1.timestamp > Time.currentMillis();
}
function _temp3(prev) {
  return prev + 1;
}